import React from "react"
import { Typography } from "@mui/material"
import { useField } from "formik"

import { CreateUpdateBookingAttendee } from "../../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { SelectedMainUserState } from "../../../createOrderTypes"
import { AccountingDimensionController } from "../../form/AccountingDimensionController"
import { DynamicDateTime } from "../../form/DynamicDateTime"
import { DynamicFormField, DynamicFormFieldGroupWrap } from "../../form/DynamicFormField"
import { isAccountingDimensionKey } from "../../form/helpers"
import { ResponsibleAutocomplete } from "../../form/ResponsibleAutocomplete"
import {
    DeliveryFulfillmentType,
    DynamicConfigField,
    DynamicConfigKey,
    MeetingServiceConfig,
} from "../../form/formTypes"

import { BuyerPartiesController } from "../billReceiver/BuyerPartiesController"
import { DeliveryLocationAutocomplete } from "../deliveryLocation/DeliveryLocationAutocomplete"
import { OtherDeliveryLocationField } from "../deliveryLocation/OtherDeliveryLocationField"
import { DeliveryNote } from "../deliveryNote/DeliveryNote"
import { Recurrence } from "../meetingDetails/Recurrence"
import { PaymentAutocomplete } from "../payment/PaymentAutocomplete"
import { Participants } from "../participants/Participants"
import { SatelliteCanteensContainer } from "../satellites/SatelliteCanteensContainer"

type FieldRendererProps = {
    config: DynamicConfigField
    groupTitle?: string
    meetingServiceConfiguration: MeetingServiceConfig
    user: SelectedMainUserState
    defaultButtonClass?: string
    deliveryFields: {
        deliveryLocations?: DynamicConfigField
        otherDeliveryLocation?: DynamicConfigField
    }
    showOtherLocationField: boolean
    setShowOtherLocationField: (v: boolean) => void
    isCreateBuyerParty?: boolean
    hasMeetingLocationRequired?: boolean
    isSpecialOrder?: boolean
}

function FieldRendererInner({
    config,
    groupTitle,
    meetingServiceConfiguration,
    user,
    defaultButtonClass,
    deliveryFields,
    showOtherLocationField,
    setShowOtherLocationField,
    isCreateBuyerParty,
    hasMeetingLocationRequired,
    isSpecialOrder,
}: FieldRendererProps) {
    const [{ value: deliveryType = "" }] = useField<DeliveryFulfillmentType>(DynamicConfigKey.DELIVERY_TYPE)
    const [{ value: registerParticipantsValue }] = useField<CreateUpdateBookingAttendee[]>(
        DynamicConfigKey.REGISTER_PARTICIPANTS,
    )

    if (config.key === DynamicConfigKey.MEETING_START_TIME || config.key === DynamicConfigKey.MEETING_END_TIME) {
        if (!config.active) return null
        const configWithInputField = config.inputField
            ? config
            : { ...config, inputField: { type: "dateTime" as const } }
        return <DynamicDateTime config={configWithInputField} />
    }

    if (!config.active) {
        return null
    }

    if (config.key === DynamicConfigKey.REGISTER_PARTICIPANTS) {
        const count = Array.isArray(registerParticipantsValue) ? registerParticipantsValue.length : 0

        return (
            <>
                {count > 0 && (
                    <LocalizedStrict
                        id={
                            count > 1
                                ? "create-order-participants-count-list"
                                : "create-order-participants-count-one-item"
                        }
                        vars={{ count }}
                    >
                        <Typography variant="body2">{count}</Typography>
                    </LocalizedStrict>
                )}
                <Participants meetingServiceConfiguration={meetingServiceConfiguration} />
            </>
        )
    }

    if (config.key === DynamicConfigKey.MERCHANT_ORDER_NOTE) {
        return <DeliveryNote meetingServiceConfiguration={meetingServiceConfiguration} />
    }

    if (config.key === DynamicConfigKey.DELIVERY_LOCATIONS) {
        if (
            (deliveryType === DeliveryFulfillmentType.DELIVERY || hasMeetingLocationRequired) &&
            deliveryFields.deliveryLocations
        ) {
            const isMeetingLocation = hasMeetingLocationRequired && deliveryType !== DeliveryFulfillmentType.DELIVERY
            return (
                <DeliveryLocationAutocomplete
                    userId={user.userId}
                    config={config}
                    isMeetingLocation={isMeetingLocation}
                />
            )
        }
        return null
    }

    if (config.key === DynamicConfigKey.OTHER_DELIVERY_LOCATION) {
        if (
            (deliveryType === DeliveryFulfillmentType.DELIVERY || hasMeetingLocationRequired) &&
            deliveryFields.otherDeliveryLocation
        ) {
            const isMeetingLocation = hasMeetingLocationRequired && deliveryType !== DeliveryFulfillmentType.DELIVERY
            return (
                <OtherDeliveryLocationField
                    config={config}
                    showOtherLocationField={showOtherLocationField}
                    setShowOtherLocationField={setShowOtherLocationField}
                    defaultButtonClass={defaultButtonClass}
                    isMeetingLocation={isMeetingLocation}
                />
            )
        }
        return null
    }

    if (config.key === DynamicConfigKey.MEETING_RESPONSIBLE) {
        return (
            <DynamicFormFieldGroupWrap config={config}>
                <ResponsibleAutocomplete
                    name={DynamicConfigKey.MEETING_RESPONSIBLE}
                    label={config.label || config.description}
                    user={user}
                />
            </DynamicFormFieldGroupWrap>
        )
    }

    if (config.key === DynamicConfigKey.RECURRENCY) {
        if (isSpecialOrder) return null
        return (
            <Recurrence
                title={groupTitle}
                label={config.label}
                description={config.description}
                defaultButtonClass={defaultButtonClass}
            />
        )
    }

    if (config.key === DynamicConfigKey.CREATE_BUYER_PARTY) {
        return null
    }
    if (config.key === DynamicConfigKey.BUYER_PARTIES) {
        return <BuyerPartiesController config={config} user={user} isCreateBuyerParty={isCreateBuyerParty} />
    }

    // if (config.key === DynamicConfigKey.PAYMENT_METHOD) {
    //     return <PaymentAutocomplete config={config} />
    // }

    if (isAccountingDimensionKey(config.key)) return <AccountingDimensionController config={config} user={user} />

    if (config.key === DynamicConfigKey.SATELLITES) {
        return <SatelliteCanteensContainer config={config} user={user} />
    }

    return <DynamicFormField config={config} user={user} />
}

export const FieldRenderer = React.memo(FieldRendererInner)
